import React, { useState, useContext, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import './style.css';
import '../../styles/page-titles.css';
import '../../styles/modern-settings.css';
import { SidebarContext } from "../../AppRoutes";
import PermissionGate from "../../services/PermissionGate";
import { FaMapMarkerAlt, FaClock, FaTruck, FaBuilding, FaMotorcycle, FaSave, FaCoins, FaHandHoldingUsd } from "react-icons/fa";
import { IoStorefrontOutline } from "react-icons/io5";
import { toast } from "react-toastify";

// Importar componentes existentes
import EnderecoEstabelecimento from "../Endereco/enderecoEstabelecimento";
import HorarioFuncionamento from "../HorarioFuncionamento";
import AddRaioEntrega from "../Mapa/addRaioEntrega";
import FormasTiposEntrega from "../FormasTiposEntrega";
import MoedaConfig from "./MoedaConfig";
import GorjetaConfig from "./GorjetaConfig";

// Importar API
import { getDadosLoja, updateDadosLoja } from "../../services/api";
import PhoneInput from "../../components/PhoneInput";
import { toE164, fromE164 } from "../../utils/phone";
import regionConfig from "../../config/regionConfig";

const InformacoesLoja = () => {
    const { t } = useTranslation('storeInfo');
    const { sidebar } = useContext(SidebarContext);
    const [activeTab, setActiveTab] = useState('dados');

    // Estado para aba Dados da Loja
    const [dadosLoja, setDadosLoja] = useState({
        cnpj: '',
        name: '',
        email: '',
        telefone: ''
    });
    const [loadingDados, setLoadingDados] = useState(false);
    const [savingDados, setSavingDados] = useState(false);

    // Carregar dados da loja
    const carregarDadosLoja = useCallback(async () => {
        setLoadingDados(true);
        try {
            const response = await getDadosLoja();
            if (response.data) {
                setDadosLoja({
                    cnpj: response.data.cnpj || '',
                    name: response.data.name || '',
                    email: response.data.email || '',
                    telefone: toE164(response.data.telefone_completo || response.data.telefone) || ''
                });
            }
        } catch (error) {
            console.error('Erro ao carregar dados da loja:', error);
            toast.error(t('toasts.loadError', 'Erro ao carregar dados'));
        } finally {
            setLoadingDados(false);
        }
    }, [t]);

    useEffect(() => {
        if (activeTab === 'dados') {
            carregarDadosLoja();
        }
    }, [activeTab, carregarDadosLoja]);

    // Salvar dados da loja
    const handleSaveDadosLoja = async () => {
        setSavingDados(true);
        try {
            if (!dadosLoja.name.trim()) {
                toast.error(t('toasts.nameRequired', 'Nome da loja e obrigatorio'));
                setSavingDados(false);
                return;
            }

            await updateDadosLoja({
                name: dadosLoja.name,
                email: dadosLoja.email,
                telefone: fromE164(dadosLoja.telefone)
            });

            toast.success(t('toasts.saveSuccess', 'Dados salvos com sucesso!'));
        } catch (error) {
            console.error('Erro ao salvar dados da loja:', error);
            toast.error(t('toasts.saveError', 'Erro ao salvar dados'));
        } finally {
            setSavingDados(false);
        }
    };

    // Handler para mudanca de inputs
    const handleInputChange = (field, value) => {
        setDadosLoja(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Formatar CNPJ para exibicao
    const formatCNPJ = (cnpj) => {
        if (!cnpj) return '';
        const cleaned = cnpj.replace(/\D/g, '');
        if (cleaned.length !== 14) return cnpj;
        return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    };


    // Obter subtitulo baseado na aba ativa
    const getSubtitle = () => {
        const subtitles = {
            dados: t('storeData.subtitle', 'Informacoes basicas do seu estabelecimento'),
            endereco: t('address.subtitle', 'Localizacao do seu estabelecimento'),
            horarios: t('businessHours.pageSubtitle', 'Configure os horarios de atendimento'),
            regioes: t('deliveryMap.subtitle', 'Configure as areas de entrega'),
            entrega: t('deliveryTypes.subtitle', 'Configure os tipos de servico'),
            moeda: t('currency.subtitle', 'Defina em que moeda seus precos estao e como exibi-los ao cliente.'),
            gorjeta: t('tip.subtitle', 'Taxa de servico sugerida no fechamento da conta.')
        };
        return subtitles[activeTab] || '';
    };

    return (
        <PermissionGate permissions={['default']}>
            <div
                className="modern-settings-container"
                style={{
                    marginLeft: sidebar ? '250px' : '100px',
                    width: sidebar ? 'calc(100% - 250px)' : 'calc(100% - 100px)'
                }}
            >
                {/* Header - Padrao Global de Paginas */}
                <div className="page-title-with-subtitle">
                    <div className="page-title-with-icon">
                        <div className="page-icon"><IoStorefrontOutline /></div>
                        <h1 className="page-title">{t('pageTitle', 'Informacoes da Loja')}</h1>
                    </div>
                    <p className="page-subtitle">{getSubtitle()}</p>
                </div>

                {/* Sistema de Abas */}
                <div className="info-loja-tabs-container">
                    <button
                        className={`info-loja-tab-button ${activeTab === 'dados' ? 'active' : ''}`}
                        onClick={() => setActiveTab('dados')}
                    >
                        <span className="info-loja-tab-number">1</span>
                        <FaBuilding className="info-loja-tab-icon" />
                        {t('tabs.storeData', 'Dados da Loja')}
                    </button>
                    <button
                        className={`info-loja-tab-button ${activeTab === 'endereco' ? 'active' : ''}`}
                        onClick={() => setActiveTab('endereco')}
                    >
                        <span className="info-loja-tab-number">2</span>
                        <FaMapMarkerAlt className="info-loja-tab-icon" />
                        {t('tabs.address', 'Endereco')}
                    </button>
                    <button
                        className={`info-loja-tab-button ${activeTab === 'horarios' ? 'active' : ''}`}
                        onClick={() => setActiveTab('horarios')}
                    >
                        <span className="info-loja-tab-number">3</span>
                        <FaClock className="info-loja-tab-icon" />
                        {t('tabs.businessHours', 'Horario de Funcionamento')}
                    </button>
                    <button
                        className={`info-loja-tab-button ${activeTab === 'regioes' ? 'active' : ''}`}
                        onClick={() => setActiveTab('regioes')}
                    >
                        <span className="info-loja-tab-number">4</span>
                        <FaTruck className="info-loja-tab-icon" />
                        {t('tabs.deliveryMap', 'Mapa de Entrega')}
                    </button>
                    <button
                        className={`info-loja-tab-button ${activeTab === 'entrega' ? 'active' : ''}`}
                        onClick={() => setActiveTab('entrega')}
                    >
                        <span className="info-loja-tab-number">5</span>
                        <FaMotorcycle className="info-loja-tab-icon" />
                        {t('tabs.deliveryTypes', 'Formas e Tipos de Entrega')}
                    </button>
                    {/* Só aparece onde faz sentido: no Brasil toda loja opera em BRL. */}
                    {regionConfig.features.multiCurrency && (
                        <button
                            className={`info-loja-tab-button ${activeTab === 'moeda' ? 'active' : ''}`}
                            onClick={() => setActiveTab('moeda')}
                        >
                            <span className="info-loja-tab-number">6</span>
                            <FaCoins className="info-loja-tab-icon" />
                            {t('tabs.currency', 'Moeda e Cotacao')}
                        </button>
                    )}
                    {/* Gorjeta serve a qualquer regiao -- nao e gated por multiCurrency. */}
                    <button
                        className={`info-loja-tab-button ${activeTab === 'gorjeta' ? 'active' : ''}`}
                        onClick={() => setActiveTab('gorjeta')}
                    >
                        <span className="info-loja-tab-number">{regionConfig.features.multiCurrency ? 7 : 6}</span>
                        <FaHandHoldingUsd className="info-loja-tab-icon" />
                        {t('tabs.tip', 'Gorjeta')}
                    </button>
                </div>

                {/* Conteudo das Abas */}
                <div className="modern-settings-content">
                    {/* Aba 1: Dados da Loja */}
                    {activeTab === 'dados' && (
                        <div className="info-loja-dados-wrapper">
                            {loadingDados ? (
                                <div className="modern-loading">
                                    <div className="modern-spinner"></div>
                                    <p>{t('loading', 'Carregando...')}</p>
                                </div>
                            ) : (
                                <div className="info-loja-dados-content">
                                    {/* Header da Secao com Botao */}
                                    <div className="info-loja-dados-section-header">
                                        <div className="info-loja-dados-header">
                                            <div className="info-loja-dados-header-icon">
                                                <FaBuilding />
                                            </div>
                                            <div>
                                                <h2>{t('storeData.title', 'Dados da Loja')}</h2>
                                                <p>{t('storeData.subtitle', 'Informacoes basicas do seu estabelecimento')}</p>
                                            </div>
                                        </div>
                                        <button
                                            className="modern-btn modern-btn-save"
                                            onClick={handleSaveDadosLoja}
                                            disabled={savingDados || loadingDados}
                                        >
                                            <FaSave />
                                            {savingDados ? t('storeData.saving', 'Salvando...') : t('storeData.saveButton', 'Salvar Dados')}
                                        </button>
                                    </div>

                                    {/* Grid de Campos */}
                                    <div className="info-loja-dados-grid">
                                        {/* CNPJ - Somente leitura (BR only) */}
                                        {regionConfig.features.cpfCnpj && (
                                        <div className="info-loja-dados-field">
                                            <label className="info-loja-dados-label">
                                                CNPJ
                                                <span className="modern-badge">{t('storeData.form.readOnly', 'Somente Leitura')}</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="info-loja-dados-input readonly"
                                                value={formatCNPJ(dadosLoja.cnpj)}
                                                disabled
                                                readOnly
                                            />
                                        </div>
                                        )}

                                        {/* Telefone */}
                                        <div className="info-loja-dados-field">
                                            <label className="info-loja-dados-label">{t('storeData.form.phone', 'Telefone')}</label>
                                            <PhoneInput
                                                className="info-loja-dados-input"
                                                value={dadosLoja.telefone || ''}
                                                onChange={(val) => handleInputChange('telefone', val || '')}
                                                placeholder={t('storeData.form.phonePlaceholder', '00000000000')}
                                            />
                                        </div>

                                        {/* Nome da Loja */}
                                        <div className="info-loja-dados-field full-width">
                                            <label className="info-loja-dados-label">
                                                {t('storeData.form.storeName', 'Nome da Loja')} <span className="required">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="info-loja-dados-input"
                                                value={dadosLoja.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                placeholder={t('storeData.form.storeNamePlaceholder', 'Digite o nome da sua loja')}
                                            />
                                        </div>

                                        {/* Email */}
                                        <div className="info-loja-dados-field full-width">
                                            <label className="info-loja-dados-label">{t('storeData.form.email', 'E-mail')}</label>
                                            <input
                                                type="email"
                                                className="info-loja-dados-input"
                                                value={dadosLoja.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                placeholder={t('storeData.form.emailPlaceholder', 'email@exemplo.com')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Aba 2: Endereco */}
                    {activeTab === 'endereco' && (
                        <EnderecoEstabelecimento embedded={true} />
                    )}

                    {/* Aba 3: Horarios */}
                    {activeTab === 'horarios' && (
                        <HorarioFuncionamento embedded={true} />
                    )}

                    {/* Aba 4: Regioes de Entrega */}
                    {activeTab === 'regioes' && (
                        <AddRaioEntrega embedded={true} />
                    )}

                    {/* Aba 5: Entrega e Retirada */}
                    {activeTab === 'entrega' && (
                        <FormasTiposEntrega embedded={true} />
                    )}

                    {/* Aba 6: Moeda e Cotacao (so nas regioes multi-moeda) */}
                    {activeTab === 'moeda' && regionConfig.features.multiCurrency && (
                        <MoedaConfig />
                    )}

                    {/* Aba: Gorjeta */}
                    {activeTab === 'gorjeta' && (
                        <GorjetaConfig />
                    )}
                </div>
            </div>
        </PermissionGate>
    );
};

export default InformacoesLoja;
